import ApplicationController from './application_controller';
import pdfjsLib from "pdfjs-dist/webpack";
import tippy, { hideAll } from "tippy.js";
import Panzoom from '@panzoom/panzoom';
import { v4 as uuidv4 } from 'uuid';
import PanningHandler from "../handlers/panning_handler";
import BatchInsertHandler from "../handlers/batch_insert_handler";
import BatchMovementHandler from "../handlers/batch_move_handler";
import consumer from '../channels/consumer';

export default class extends ApplicationController {
  static targets = [
    "canvas",
    "zoomIn",
    "zoomOut",
    "overlay",
    "toggleButton",
    "floorplan",
    "batchNamePatternLegend",
    "batchInsertOptions",
    "batchInsertCheckbox",
    "batchInsertSensorsCount",
    "batchInsertNamePattern",
    "batchInsertRows",
    "batchInsertColumns",
    "toggleBatchMoveButton",
    "autoSelectBusCheckbox",
    "busSelectBox"
  ];

  initialize() {
    this.scaleStep = 0.25;
    this.toggledProductId = null;
    this.debounceTimer = null;
    this.iconScaleStep = 0.05;
    this.iconInitialWidth = 60;
    this.iconInitialHeight = 60;

    this.panningHandler = new PanningHandler(this);
    this.batchInsertHandler = new BatchInsertHandler(this);
    this.batchMoveHandler = new BatchMovementHandler(this);

    const proposalId = this.element.dataset.proposalId;
    const floorplanId = this.getActiveFloorplanId();
    const controller = this;

    this._floorplanEditorSubscription = consumer.subscriptions.create(
      { channel: 'FloorplanEditorChannel', proposal_id: proposalId, floorplan_id: floorplanId },
      {
        received(data) {
          if (data.cableReady) {
            CableReady.perform(data.operations);
            return;
          }

          if (data.type === 'placement:added') {
            const overlay = document.getElementById(data.overlay_id);
            if (!overlay) return;
            const placeholder = document.getElementById(data.temporary_element_id);
            if (!placeholder) {
              overlay.insertAdjacentHTML('beforeend', data.html);
            }
            return;
          }

          if (data.type === 'batch:started') {
            // Jiné okno zahájilo batch insert - zobrazit readonly progress modal
            if (!controller._isBatchInitiator) {
              const modalEl = controller.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
              const modalController = modalEl
                ? controller.application.getControllerForElementAndIdentifier(modalEl, 'modal')
                : null;
              if (modalController) {
                modalController.showBatchProgress(data.total, { readOnly: true, userName: data.user_name });
              }
            }
            return;
          }

          if (data.type === 'batch:finished') {
            // Batch dokončen v jiném okně - zavřít readonly modal a refreshnout stránku
            if (!controller._isBatchInitiator) {
              const modalEl = controller.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
              const modalController = modalEl
                ? controller.application.getControllerForElementAndIdentifier(modalEl, 'modal')
                : null;
              if (modalController) modalController.closeModal();
              window.location.reload();
            }
            return;
          }
        }
      }
    );

    this._onBatchProgress = (e) => {
      controller._onBatchProgressUpdate(e.detail.current, e.detail.total);
    };
    document.addEventListener('batch-progress:update', this._onBatchProgress);

    this._onBatchProgressError = (e) => {
      const modalEl = controller.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
      const modalController = modalEl
        ? controller.application.getControllerForElementAndIdentifier(modalEl, 'modal')
        : null;
      if (modalController) {
        modalController.showBatchProgressError(e.detail.message);
      }
    };
    document.addEventListener('batch-progress:error', this._onBatchProgressError);

    this._onBatchProgressLocked = (e) => {
      const modalEl = controller.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
      const modalController = modalEl
        ? controller.application.getControllerForElementAndIdentifier(modalEl, 'modal')
        : null;
      if (modalController) {
        modalController.showBatchProgress(e.detail.total, { readOnly: true, userName: e.detail.user_name });
      }
    };
    document.addEventListener('batch-progress:locked', this._onBatchProgressLocked);

    this.canvasTargets.forEach(canvasTarget => {
      this.renderPage(canvasTarget, 1.0);
    })

    for (const button of this.toggleButtonTargets) {
      tippy(button, {
        content: button.dataset.installationNotes,
      });
    }

    tippy(this.batchNamePatternLegendTarget, {
      content: this.batchNamePatternLegendTarget.dataset.tooltip,
    });

    this.batchMoveHandler.restoreInitialBatchMoveState();
  }

  disconnect() {
    this.panningHandler.removePanning();
    if (this._floorplanEditorSubscription) {
      this._floorplanEditorSubscription.unsubscribe();
    }
    document.removeEventListener('batch-progress:update', this._onBatchProgress);
    document.removeEventListener('batch-progress:error', this._onBatchProgressError);
    document.removeEventListener('batch-progress:locked', this._onBatchProgressLocked);
  }

  _onBatchProgressUpdate(current, total) {
    const modalEl = this.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
    const modalController = modalEl
      ? this.application.getControllerForElementAndIdentifier(modalEl, 'modal')
      : null;
    if (modalController) {
      modalController.updateBatchProgress(current, total);
    }
  }

  getAllPlacements(floorplanWrapper) {
    return floorplanWrapper.querySelectorAll('.point.draggable');
  }

  getActiveFloorplanId() {
    let currentTab = document.querySelector('.controls ul li.active');
    return currentTab.getAttribute('data-tab-target').replace("floorplan-", "");
  }

  removeActiveStylesFromProductButtons() {
    for (let button of this.toggleButtonTargets) {
      button.style.color = button.dataset.color;
      button.style.backgroundColor = 'white';
      button.style.backgroundImage = `url(${button.dataset.backgroundImage})`;
      this.overlayTargets.forEach(overlayTarget => { overlayTarget.style.cursor = `auto`; });
    }
  }

  toggle(e) {
    const proposalLocked = document.querySelector('input[name="proposal_locked"]')?.checked
    if (proposalLocked) {
      return;
    }
    this.removeActiveStylesFromProductButtons();
    if (this.toggledProductId === event.target.dataset.productId) {
      this.toggledProductId = null;
      this.batchInsertHandler.removeCursor()
      this.batchInsertHandler.removeCursorListener(this.overlayTargets);
    } else {
      this.toggledProductId = event.target.dataset.productId;
      event.target.style.color = "white";
      event.target.style.backgroundColor = event.target.dataset.color;
      event.target.style.backgroundImage = `url(${event.target.dataset.inverseBackgroundImage})`;
      this.overlayTargets.forEach(overlayTarget => { overlayTarget.style.cursor = `url(${event.target.dataset.cursorImage}) 32 32, auto`; });

      if (this.batchInsertCheckboxTarget.checked) {
        this.batchInsertHandler.updateCursorElement(e);
        this.batchInsertHandler.addCursorListener(this.overlayTargets);
      }

      hideAll();
    }
  }


  activateProductButton(productButton) {
    productButton.style.color = "white";
    productButton.style.backgroundColor = productButton.dataset.color;
    productButton.style.backgroundImage = `url(${productButton.dataset.inverseBackgroundImage})`;
  }

  add(event) {
    if (this.toggledProductId === null || this.overlayTarget.disabled === true) {
      return;
    }

    if (this.batchInsertCheckboxTarget.checked && !this.batchInsertHandler.canInsertInBatch) {
      return;
    }

    let spinnerElement = event.currentTarget.querySelector('#spinner');
    if (document.querySelector('input[id="floorplan.editor.add_batch"]').checked) {
      this.overlayTargets.forEach(overlayTarget => { overlayTarget.disabled = false; });
      if (spinnerElement !== null) {
        this.spinnerOff(spinnerElement);
      }
    } else {
      this.overlayTargets.forEach(overlayTarget => { overlayTarget.disabled = true; });
      this.spinnerOn(spinnerElement);
    }
    var rect = event.target.getBoundingClientRect();
    var x = (event.clientX - rect.left) / event.target.offsetWidth;
    var y = (event.clientY - rect.top) / event.target.offsetHeight;
    var newPlacementDiv = document.createElement('div');
    newPlacementDiv.id = 'point-' + uuidv4();
    newPlacementDiv.innerHTML = '<div class="point draggable panzoom-exclude">??</div></div>';
    newPlacementDiv.clientX = event.clientX;
    newPlacementDiv.clientY = event.clientY;
    event.target.appendChild(newPlacementDiv);
    var currentTab = document.querySelector(".controls ul li.active");
    var busId = document.querySelector("#busable_id").value;
    if (this.busSelectBoxTarget.disabled) {
      busId = null;
    }
    let controller = this;
    let placementNumber = document.querySelector("#placement_number").value;
    let overlayTargets = this.overlayTargets;
    let productId = this.toggledProductId;

    let scaleElement = document.getElementById('floorplan-icon-scale');
    let scaleTo = parseFloat(scaleElement.dataset.scale) + this.iconScaleStep;

    const rowGap = this.iconInitialHeight / event.target.offsetHeight;
    const columnGap = this.iconInitialWidth / event.target.offsetWidth;

    const batchInsertOptions = {
      batch_insert: this.batchInsertCheckboxTarget.checked,
      sensors_count: parseInt(this.batchInsertSensorsCountTarget.value),
      name_pattern: this.batchInsertNamePatternTarget.value,
      scaling_factor: scaleTo,
      row_gap: rowGap,
      column_gap: columnGap,
      insert_options: {
        rows: parseInt(this.batchInsertRowsTarget.value),
        columns: parseInt(this.batchInsertColumnsTarget.value),
      },
    };
    let nextPlacementNumber = document.querySelector('#placement_number');
    nextPlacementNumber.value = parseInt(nextPlacementNumber.value) + 1;
    let addNameByProduct = document.querySelector("input[id='floorplan.editor.add_name_by_product']").checked;

    if (batchInsertOptions.batch_insert) {
      const modalEl = this.element.closest('[data-controller~="modal"]') || document.querySelector('[data-controller~="modal"]');
      const modalController = modalEl
        ? this.application.getControllerForElementAndIdentifier(modalEl, 'modal')
        : null;
      if (modalController) {
        modalController.showBatchProgress(batchInsertOptions.sensors_count);
      }
      this._isBatchInitiator = true;
    }

    this.stimulate("PlacementReflex#add", event.target, busId, productId, x, y, placementNumber, batchInsertOptions, newPlacementDiv.id, addNameByProduct)
      .then(payload => {
        const success_event = new CustomEvent('save-indicator:success', { detail: payload });
        window.dispatchEvent(success_event);
      })
      .catch(payload => {
        const failed_event = new CustomEvent('save-indicator:failed', { detail: payload });
        window.dispatchEvent(failed_event);
      })
      .finally(() => {
        this._isBatchInitiator = false;
        overlayTargets.forEach(overlayTarget => { overlayTarget.disabled = false; });
        if (spinnerElement !== null) {
          // the variable is defined
          this.spinnerOff(spinnerElement);
        }

        this.batchInsertHandler.removeCursorListener(this.overlayTargets);
      });
  }
  toggleAutoSelectBusCheckbox(event) {
    if (this.autoSelectBusCheckboxTarget) {
      this.autoSelectBusCheckboxTarget.checked = !this.autoSelectBusCheckboxTarget.checked
      this.toggleAutoSelectBus(event);
    } else {
      this.appendLog("Unable to change checkbox")
    }
  }

  toggleAutoSelectBus(event) {
    if (this.autoSelectBusCheckboxTarget) {
      this.busSelectBoxTarget.disabled = this.autoSelectBusCheckboxTarget.checked;
    } else {
      this.appendLog("Unable to change checkbox")
    }
  }

  zoomIn(event) {
    this.zoomInTarget.disabled = true;
    this.zoomOutTarget.disabled = true;
    let placementsDiv = event.currentTarget.closest('.placements')
    let scaleStep = parseFloat(document.querySelector('#scale_step').value);
    let scale = parseFloat(placementsDiv.querySelector('#floorplan-scale').dataset.scale);
    scale = scale + scaleStep;
    placementsDiv.querySelector('#floorplan-scale').dataset.scale = scale;
    let floorplanId = placementsDiv.id.replace('floorplan-', '');
    console.log(scale);
    this.renderPage(placementsDiv.querySelector('#canvas-' + floorplanId), scale);
  }

  zoomOut() {
    this.zoomInTarget.disabled = true;
    this.zoomOutTarget.disabled = true;
    let placementsDiv = event.currentTarget.closest('.placements')
    let scaleStep = parseFloat(document.querySelector('#scale_step').value);
    let scale = parseFloat(placementsDiv.querySelector('#floorplan-scale').dataset.scale);
    if (scale - scaleStep > 0) {
      scale -= scaleStep;
      placementsDiv.querySelector('#floorplan-scale').dataset.scale = scale;
      let floorplanId = placementsDiv.id.replace('floorplan-', '');
      console.log(scale);
      this.renderPage(placementsDiv.querySelector('#canvas-' + floorplanId), scale);
    } else {
      this.zoomInTarget.disabled = false;
    }
  }

  increaseIconSize() {
    let scaleElement = event.currentTarget.closest('#floorplan-icon-scale');
    let floorplanId = scaleElement.closest('.placements').id.replace("floorplan-", "");
    let scaleTo = parseFloat(scaleElement.dataset.scale) + this.iconScaleStep;
    scaleElement.dataset.scale = scaleTo
    this.resetAndStartDebounceTimer(scaleElement.dataset.scale, floorplanId);
    this.scaleFloorplanPlacements(scaleTo, floorplanId);
  }

  decreaseIconSize() {
    let scaleElement = event.currentTarget.closest('#floorplan-icon-scale');
    let floorplanId = scaleElement.closest('.placements').id.replace("floorplan-", "");
    let scaleTo = parseFloat(scaleElement.dataset.scale) - this.iconScaleStep;
    scaleElement.dataset.scale = scaleTo
    this.resetAndStartDebounceTimer(scaleElement.dataset.scale, floorplanId);
    this.scaleFloorplanPlacements(scaleTo, floorplanId);
  }

  scaleFloorplanPlacements(scale, floorplanId) {
    let floorplanWrapper = document.querySelector('#floorplan-' + floorplanId).querySelector('.floorplan .wrapper');
    let placementsWithActiveFloorplan = this.getAllPlacements(floorplanWrapper);

    placementsWithActiveFloorplan.forEach(placement => {
      const outerWidth = parseFloat(floorplanWrapper.dataset.iconSizeOuterWidth) * scale;
      placement.style['width'] = outerWidth + 'px';
      placement.style['background-size'] = floorplanWrapper.dataset.iconSizeWidth * scale + 'px ' + floorplanWrapper.dataset.iconSizeHeight * scale + 'px';
      placement.style['left'] = 'calc(' + placement.dataset.x * 100 + '% - ' + parseFloat(floorplanWrapper.dataset.iconSizeOuterWidth) * scale / 2 + 'px)';
      placement.style['top'] = 'calc(' + placement.dataset.y * 100 + '% - ' + parseFloat(floorplanWrapper.dataset.iconSizeHeight) * scale / 2 + 'px)';
      placement.style['height'] = floorplanWrapper.dataset.iconSizeHeight * scale + 'px';
      let sensor = placement.querySelector('.sensor');

      if (sensor) {
        sensor.style['font-size'] = floorplanWrapper.dataset.iconSizeFontSize * scale + 'px';
        sensor.style['height'] = floorplanWrapper.dataset.iconSizeHeight * scale + 'px';
        sensor.style['line-height'] = sensor.style['height'];
        sensor.style['border-radius'] = 10 * scale + 'px';
        sensor.style['top'] = 'calc(-20px - ' + (floorplanWrapper.dataset.iconSizeHeight * scale - floorplanWrapper.dataset.iconSizeHeight) + 'px)';
      } else {
        let centralUnit = placement.querySelector('.central-unit');
        centralUnit.style['font-size'] = floorplanWrapper.dataset.iconSizeFontSize * scale + 'px';
        centralUnit.style['height'] = floorplanWrapper.dataset.iconSizeHeight * scale + 'px';
        centralUnit.style['line-height'] = centralUnit.style['height'];
        centralUnit.style['width'] = floorplanWrapper.dataset.iconSizeWidth * scale * 2 + 'px';
        centralUnit.style['border-radius'] = 10 * scale + 'px';

      }
    });
  }

  resetAndStartDebounceTimer(currentScale) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => { this.changeIconSize(currentScale); }, 300);
  }

  changeIconSize(scale) {
    this.stimulate('PlacementReflex#update_sensors', {
      scale: scale,
      floorplan_id: this.getActiveFloorplanId()
    }
    ).then((payload) => {
      console.log("Scale icons saved for floorplan ")
    })

  }

  renderPage(canvas, scale) {
    // display: 'none' implies no size and thus the element can be positioned via interactjs
    $('.point').css('visibility', 'hidden');
    var controller = this;

    var loadingTask = pdfjsLib.getDocument(canvas.dataset.fileUrl);
    loadingTask.promise.then(function (pdf) {
      var page = pdf.getPage(1);
      page.then(function (page) {
        var viewport = page.getViewport({ scale: scale });

        var context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        var renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        var renderTask = page.render(renderContext);
        renderTask.promise.then(function () {
          $('.point').css('visibility', 'visible');
          controller.zoomInTarget.disabled = false;
          controller.zoomOutTarget.disabled = false;
        });
      });
      document.querySelector('.floorplan').style = '';
    });
  }

  toggleMinimizer(event) {
    document.querySelector('.placement-maximize-control').classList.toggle('hidden');
    document.querySelector('.placement-control').classList.toggle('hidden');
  }

  fullScreen(event) {
    document.querySelector("body > main > div > div.proposal").classList.toggle('fullscreen');
    document.querySelectorAll(".placements").forEach(function (floorplan) {
      floorplan.classList.toggle('fullscreen');
    });
    // show button to select products
    document.querySelector('.placement-maximize-control').classList.toggle('hidden');
    // show button to hide/show products in fullscreen
    document.querySelector('.placement-minimize-control').classList.toggle('hidden');
    // hide select products
    document.querySelector('.placement-control').classList.toggle('hidden');
  }

  updateCursorElement(e) {
    this.batchInsertHandler.updateCursorElement(e);
  }

  toggleBatchInsertOptions(e) {
    if (this.batchInsertCheckboxTarget.checked) {
      this.batchInsertOptionsTarget.classList.remove('hidden');
      this.batchInsertHandler.updateCursorElement(e);
      this.batchInsertHandler.addCursorListener(this.overlayTargets);
    } else {
      this.batchInsertOptionsTarget.classList.add('hidden');
      this.batchInsertHandler.removeCursorListener(this.overlayTargets);
      this.batchInsertHandler.removeCursor();
    }
  }

  toggleBatchMove(e) {
    this.toggleBatchMoveButtonTarget.classList.contains('active') ?
      this.batchMoveHandler.restoreInitialBatchMoveState() : this.batchMoveHandler.prepareBatchMoveState();
  }
}
